import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Autocomplete,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import UserCreationModal from "./UserCreationModal.js";
import { useAuth } from "../../../auth";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

export default function AppointmentsModal({ open, onClose }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [appointmentType, setAppointmentType] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isCustomSlot, setIsCustomSlot] = useState(false);
  const [customTime, setCustomTime] = useState("");
  const [duration, setDuration] = useState("");
  const [timezone, setTimeZone] = useState("");
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("");

  // New state variables for register_as categories
  const [selectedRegisterAs, setSelectedRegisterAs] = useState("");
  const [availableRegisterAs, setAvailableRegisterAs] = useState([]);

  // State variables for expert details and next available slot
  const [expertDetails, setExpertDetails] = useState(null);
  const [nextAvailableSlot, setNextAvailableSlot] = useState(null);
  const [loadingExpertDetails, setLoadingExpertDetails] = useState(false);

  // Autocomplete states
  const [patientOptions, setPatientOptions] = useState([]);
  const [expertOptions, setExpertOptions] = useState([]);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [expertInputValue, setExpertInputValue] = useState("");

  const [loading, setLoading] = useState({
    patients: false,
    doctors: false,
    slots: false,
    booking: false,
  });

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const finalPrice =
    price && discount ? price - (price * discount) / 100 : price;

  // Snackbar handlers
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseModal = () => {
    // Reset form
    setSelectedPatient(null);
    setSelectedExpert(null);
    setAppointmentType("");
    setSelectedSlot("");
    setSelectedDate(new Date().toISOString().split("T")[0]);
    setDuration("");
    // setTimezone("");
    setPrice(0);
    setDiscount(0);
    setPaymentMethod("");
    setPatientInputValue("");
    setExpertInputValue("");
    setSelectedRegisterAs("");
    setAvailableRegisterAs([]);
    setExpertDetails(null);
    setNextAvailableSlot(null);

    onClose();
  };

  // Reusable function to create transaction
  const createTransaction = async (paymentMode, appointmentTime) => {
    const transactionRequestBody = {
      professional_id: selectedExpert.id.toString(),
      client_id: selectedPatient.id.toString(),
      payment_mode: paymentMode,
      start_time: appointmentTime,
      // env: "TEST",
      duration: duration || 30,
      register_as: selectedRegisterAs,
      appointment_type: appointmentType,
      consultation_type:
        appointmentType === "follow-up"
          ? "Follow up Consult"
          : "Initial Consult",
      creator_id: currentUser?.UserId,
      creator_name: currentUser?.Name
    };

    // Add price and discount for cash payments
    if (paymentMode === "cash") {
      transactionRequestBody.price = price;
      transactionRequestBody.discount = discount;
    }

    const response = await fetch(
      "https://us.uwc.world/new-uwc/uwc-expert/admin/appointment/create-transaction",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
        },
        body: JSON.stringify(transactionRequestBody),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Transaction API error! status: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.code !== 200 || !data.data) {
      throw new Error(data.message || "Failed to create transaction");
    }

    return data.data;
  };

  // Helper function to get session ID from cookies
  const getSessionId = () => {
    const cookies = document.cookie.split(";");
    const sessionCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("session_id=")
    );
    return sessionCookie ? sessionCookie.split("=")[1] : null;
  };

  // Function to fetch expert details and next available slots
  const fetchExpertDetails = async (
    expertId,
    expertTimeZone,
    expertCategory
  ) => {
    try {
      const requestBody = {
        expert_id: expertId.toString(),
        // timezone: "Australia/Adelaide", // Default timezone for API request
        user_id: currentUser?.UserId?.toString() || "167251", // Default user ID, can be made dynamic
        timezone: expertTimeZone || "",
        profession: expertCategory,
        duration: duration,
      };

      const response = await fetch(
        "https://us.uwc.world/new-uwc/uwc-expert/v2/professionals/view",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "197520923f43d10b80eff1540fa125ca8d43829a7e44cd23d32f58a76b95eb82167251",
            "X-API-Key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
            DeviceType: "web",
            DeviceId: "123",
            "User-Agent":
              "Mozilla/5.0 (Linux; Android 6.0; Nexus 5 Build/MRA58N) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Mobile Safari/537.36",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.code !== 200 || !data.data) {
        throw new Error(data.message || "Failed to fetch expert details");
      }

      return data.data;
    } catch (error) {
      console.error("Error fetching expert details:", error);
      throw error;
    }
  };

  // Helper function to handle expert selection and extract register_as categories
  const handleExpertSelection = async (expert) => {
    setSelectedExpert(expert);

    // Reset states
    setExpertDetails(null);
    setNextAvailableSlot(null);
    setSelectedRegisterAs("");
    setAvailableRegisterAs([]);
    setAppointmentType("");
    setDuration("");
    setPrice(0);
    setAvailableSlots([]);
    setSelectedSlot("");

    if (!expert || !expert.id) {
      return;
    }

    // Fetch expert details to get register_as categories and next available slot immediately
    setLoadingExpertDetails(true);
    if (expert.durations && expert.durations.length > 0) {
      const uniqueRegisterAs = [
        ...new Set(expert.durations.map((d) => d.register_as)),
      ];
      console.log("::::: ", uniqueRegisterAs);
      setAvailableRegisterAs(uniqueRegisterAs);
    }
  };

  useEffect(() => {
    if (!selectedRegisterAs || !appointmentType) return;

    const fetchData = async () => {
      try {
        const expertData = await fetchExpertDetails(
          selectedExpert.id,
          selectedExpert?.timezone,
          selectedRegisterAs
        );

        setExpertDetails(expertData);

        if (expertData.next_available_slot) {
          setNextAvailableSlot(expertData.next_available_slot || "timezone");
        }
      } catch (error) {
        console.error("Error fetching expert details:", error);
        showSnackbar(
          `Failed to fetch expert details: ${error.message}`,
          "error"
        );
      } finally {
        setLoadingExpertDetails(false);
      }
    };

    fetchData();
  }, [selectedRegisterAs, selectedExpert, appointmentType]);

  // Helper function to normalize strings for comparison
  const normalizeString = (str) =>
    (str || "").toLowerCase().replace(/[^a-z0-9]/gi, "");

  // Helper function to format next available slot
  const formatNextAvailableSlot = (slotDateTime) => {
    if (!slotDateTime) return null;

    try {
      const date = new Date(slotDateTime);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

      // Skip Sunday (0) and Monday (1)
      // if (dayOfWeek === 0 || dayOfWeek === 1) {
      //   return null;
      // }

      // Always show date in DD/MM/YYYY format regardless of whether it's today or tomorrow
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year} at ${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return slotDateTime;
    }
  };

  // Helper function to get duration for selected register_as and appointment type
  const getDurationForSelection = () => {
    if (!expertDetails || !selectedRegisterAs || !appointmentType) {
      return null;
    }

    const normalizedType = normalizeString(appointmentType);
    const matchingDuration = expertDetails.durations.find(
      (d) =>
        d.register_as === selectedRegisterAs &&
        normalizeString(d.display_text).includes(normalizedType)
    );

    return matchingDuration ? matchingDuration.duration : null;
  };

  // Helper function to get price for selected register_as and appointment type
  const getPriceForSelection = () => {
    if (!expertDetails || !selectedRegisterAs || !appointmentType) {
      return null;
    }

    const normalizedType = normalizeString(appointmentType);
    const matchingDuration = expertDetails.durations.find(
      (d) =>
        d.register_as === selectedRegisterAs &&
        normalizeString(d.display_text).includes(normalizedType)
    );

    return matchingDuration ? matchingDuration.price : null;
  };

  // Helper function to handle appointment type selection
  const handleAppointmentTypeSelection = (type) => {
    if (!type) return;

    setAppointmentType(type);

    // Get duration and price for the selected combination
    if (selectedExpert && selectedRegisterAs) {
      const normalizedType = normalizeString(type);
      const matchingDuration = selectedExpert.durations.find((d) => {
        const registerAsMatch = d.register_as === selectedRegisterAs;
        const normalizedDisplay = normalizeString(d.display_text);
        const typeMatch = normalizedDisplay.includes(normalizedType);

        return registerAsMatch && typeMatch;
      });
      if (matchingDuration) {
        setDuration(matchingDuration.duration);
        // Auto-set price for cash payments if available
        if (
          matchingDuration.price !== undefined &&
          matchingDuration.price !== null
        ) {
          setPrice(matchingDuration.price);
        }
      }
    }
  };

  // API functions
  // TODO: Replace with actual Authorization bearer token
  const searchPatients = async (query) => {
    try {
      // Get authentication token from localStorage
      const authToken = localStorage
        .getItem("Authorization")
        ?.split("Bearer ")[1];

      if (!authToken) {
        console.error("No authentication token found");
        return [];
      }

      // Check if the query looks like a phone number
      const isPhoneNumber = /^[\d\s\-\+\(\)]+$/.test(query.trim());

      // Clean phone number by removing spaces, dashes, and parentheses
      const cleanPhoneNumber = query.trim().replace(/[\s\-\+\(\)]/g, "");

      // Prepare request body based on search type
      let requestBody;
      if (isPhoneNumber && cleanPhoneNumber.length >= 10) {
        // For phone number searches, try multiple formats
        requestBody = {
          search: query.trim(),
          phone: cleanPhoneNumber,
          search_type: "phone",
        };
      } else {
        // For name/email searches
        requestBody = {
          search: query.trim(),
          search_type: "general",
        };
      }

      const response = await fetch(
        "https://cauth.unitedwecare.com/users/admin/search-client",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_ADMIN_API_KEY,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Map the API response to match the expected format
      const patients = data.data || [];
      const mappedPatients = patients.map((patient) => ({
        id: patient.user_id || patient.id,
        name: patient.name || "Unknown Patient",
        email: patient.email,
        phone: patient.phone,
        country: patient.country,
        image: patient.image,
      }));

      // If no results found and it's a phone number search, try with just the search parameter
      if (
        mappedPatients.length === 0 &&
        isPhoneNumber &&
        cleanPhoneNumber.length >= 10
      ) {
        const fallbackResponse = await fetch(
          "https://cauth.unitedwecare.com/users/admin/search-client",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_ADMIN_API_KEY,
            },
            body: JSON.stringify({ search: cleanPhoneNumber }),
          }
        );

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const fallbackPatients = fallbackData.data || [];
          const fallbackMappedPatients = fallbackPatients.map((patient) => ({
            id: patient.user_id || patient.id,
            name: patient.name || "Unknown Patient",
            email: patient.email,
            phone: patient.phone,
            country: patient.country,
            image: patient.image,
          }));
          return fallbackMappedPatients;
        }
      }

      return mappedPatients;
    } catch (error) {
      console.error("Error searching patients:", error);
      return [];
    }
  };

  const searchExperts = async (query) => {
    try {
      // Get authentication token from localStorage
      const authToken = localStorage
        .getItem("Authorization")
        ?.split("Bearer ")[1];

      if (!authToken) {
        console.error("No authentication token found");
        return [];
      }

      const requestBody = {
        search: query,
        email: currentUser.Emailid,
        expert_id: currentUser.UserId,
      };
      const response = await fetch(
        "https://cauth.unitedwecare.com/users/admin/search-expert?page=1&limit=5",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_ADMIN_API_KEY,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Map the API response to match the expected format
      const experts = data.data || [];
      const mappedExperts = experts.map((expert) => ({
        id: expert.user_id || expert.id,
        name: expert.name || "Unknown Expert",
        specialty: expert.specialty || expert.expertise || "General Practice",
        department: expert.department || expert.category || "Healthcare",
        email: expert.email,
        phone: expert.phone,
        durations: expert.durations || [], // Include the durations data
        timezone: expert.timezone || "",
      }));
      return mappedExperts;
    } catch (error) {
      console.error("Error searching experts:", error);
      return [];
    }
  };

  // Custom debounce implementation using useRef
  const patientSearchTimeoutRef = useRef(null);
  const expertSearchTimeoutRef = useRef(null);

  // Debounced search functions
  const debouncedPatientSearch = useCallback((query) => {
    // Clear existing timeout
    if (patientSearchTimeoutRef.current) {
      clearTimeout(patientSearchTimeoutRef.current);
    }

    // Set new timeout
    patientSearchTimeoutRef.current = setTimeout(async () => {
      if (query.length < 2) {
        setPatientOptions([]);
        return;
      }

      setLoading((prev) => ({ ...prev, patients: true }));
      try {
        const results = await searchPatients(query);
        setPatientOptions(results);
      } catch (error) {
        console.error("Error searching patients:", error);
        setPatientOptions([]);
      } finally {
        setLoading((prev) => ({ ...prev, patients: false }));
      }
    }, 500); // Increased debounce time to 500ms
  }, []);

  const debouncedExpertSearch = useCallback((query) => {
    // Clear existing timeout
    if (expertSearchTimeoutRef.current) {
      clearTimeout(expertSearchTimeoutRef.current);
    }

    // Set new timeout
    expertSearchTimeoutRef.current = setTimeout(async () => {
      if (query.length < 2) {
        setExpertOptions([]);
        return;
      }

      setLoading((prev) => ({ ...prev, doctors: true }));
      try {
        const results = await searchExperts(query);
        setExpertOptions(results);
      } catch (error) {
        console.error("Error searching experts:", error);
        setExpertOptions([]);
      } finally {
        setLoading((prev) => ({ ...prev, doctors: false }));
      }
    }, 500); // Increased debounce time to 500ms
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (patientSearchTimeoutRef.current) {
        clearTimeout(patientSearchTimeoutRef.current);
      }
      if (expertSearchTimeoutRef.current) {
        clearTimeout(expertSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleFindSlots = async () => {
    // Validation
    if (!selectedExpert || !selectedExpert.id) {
      showSnackbar("Please select an expert first.", "warning");
      return;
    }

    if (!selectedDate) {
      showSnackbar("Please select a date first.", "warning");
      return;
    }

    setLoading((prev) => ({ ...prev, slots: true }));

    try {
      // Build query parameters - only expert_id and date
      const params = new URLSearchParams({
        expert_id: selectedExpert.id.toString(),
        date: selectedDate,
        duration: duration,
        timezone: timezone,
      });

      const response = await fetch(
        `https://us.uwc.world/new-uwc/uwc_expert/appointment/slots?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // Process the slots data based on the actual API response format
      if (data.data && data.data.slots && data.data.slots[selectedDate]) {
        // Extract slots for the selected date
        const dateSlots = data.data.slots[selectedDate];

        // Convert datetime strings to 12-hour time format for display
        const formattedSlots = dateSlots.map((slot) => {
          const datetime = new Date(slot.time);
          const hours = datetime.getHours();
          const minutes = datetime.getMinutes();
          const ampm = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          const displayMinutes = minutes.toString().padStart(2, "0");
          return `${displayHour}:${displayMinutes} ${ampm}`;
        });

        setAvailableSlots(formattedSlots);
      } else {
        // If no slots available for the selected date
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      showSnackbar(`Failed to fetch slots: ${error.message}`, "error");
      setAvailableSlots([]);
    } finally {
      setLoading((prev) => ({ ...prev, slots: false }));
    }
  };

  const handleBook = async () => {
    // Validation logic here
    if (
      !selectedPatient ||
      !selectedExpert ||
      !selectedRegisterAs ||
      !appointmentType ||
      !paymentMethod ||
      (isCustomSlot ? !customTime : !selectedSlot)
    ) {
      showSnackbar("Please fill all required fields.", "warning");
      return;
    }

    // Validate price only if payment method is cash
    if (paymentMethod === "cash" && !price) {
      showSnackbar("Please enter a price for cash appointments.", "warning");
      return;
    }

    // Validate that we have the required IDs
    if (!selectedPatient.id || !selectedExpert.id) {
      showSnackbar("Please select both patient and expert.", "warning");
      return;
    }

    setLoading((prev) => ({ ...prev, booking: true }));

    try {
      let hours, minutes;
      
      if (isCustomSlot) {
        // For custom slots, time is already in 24-hour format
        [hours, minutes] = customTime.split(":");
        hours = parseInt(hours);
        minutes = parseInt(minutes);
      } else {
        // Convert selected slot to proper datetime format
        const [time, period] = selectedSlot.split(" ");
        [hours, minutes] = time.split(":");
        hours = parseInt(hours);

        // Convert to 24-hour format
        if (period === "PM" && hours !== 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }
      }

      // Create appointment date from selected date and time
      const appointmentDate = new Date(selectedDate);
      appointmentDate.setHours(hours, minutes, 0, 0);

      // Format as YYYY-MM-DD HH:mm:ss (local time, not UTC)
      const year = appointmentDate.getFullYear();
      const month = String(appointmentDate.getMonth() + 1).padStart(2, "0");
      const day = String(appointmentDate.getDate()).padStart(2, "0");
      const hour = String(appointmentDate.getHours()).padStart(2, "0");
      const minute = String(appointmentDate.getMinutes()).padStart(2, "0");
      const second = String(appointmentDate.getSeconds()).padStart(2, "0");
      const appointmentTime = `${year}-${month}-${day} ${hour}:${minute}:${second}`;

      if (paymentMethod === "stripe") {
        const stripeData = await createTransaction("stripe", appointmentTime);

        // Prepare appointment data for the payment status page
        const appointmentData = {
          expert_id: selectedExpert.id.toString(),
          client_id: selectedPatient.id.toString(),
          appointment_time: appointmentTime,
          // timezone: timezone,
          duration: duration || 30,
          register_as: selectedRegisterAs,
          appointment_type: appointmentType,
          consultation_type:
            appointmentType === "follow-up"
              ? "Follow up Consult"
              : "Initial Consult",
        };

        // Open payment link in new tab
        window.open(stripeData.payment_link, "_blank");

        // Redirect to payment status page
        navigate("/payment-status", {
          state: {
            transactionId: stripeData.transaction_id,
            appointmentData: appointmentData,
          },
        });

        // Close the modal
        handleCloseModal();
      } else if (paymentMethod === "cash") {
        // Handle cash payments - create transaction and redirect to payment status page

        // Step 1: Create transaction
        const transactionData = await createTransaction(
          "cash",
          appointmentTime
        );
        const transactionId = transactionData.transaction_id;

        // Prepare appointment data for the payment status page
        const appointmentData = {
          expert_id: selectedExpert.id.toString(),
          client_id: selectedPatient.id.toString(),
          appointment_time: appointmentTime,
          // timezone: timezone,
          duration: duration || 30,
          register_as: selectedRegisterAs,
          appointment_type: appointmentType,
          consultation_type:
            appointmentType === "follow-up"
              ? "Follow up Consult"
              : "Initial Consult",
        };

        // Redirect to payment status page for confirmation
        navigate("/payment-status", {
          state: {
            transactionId: transactionId,
            appointmentData: appointmentData,
            paymentMode: "cash",
          },
        });

        // Close the modal
        handleCloseModal();
      } else {
        // Handle free payments with original booking API
        const requestBody = {
          expert_id: selectedExpert.id.toString(),
          client_id: selectedPatient.id.toString(),
          appointment_time: appointmentTime,
          // timezone: timezone,
          duration: duration,
          register_as: selectedRegisterAs,
          appointment_type: appointmentType, // "follow-up" or "initial"
          consultation_type:
            appointmentType === "follow-up"
              ? "Follow up Consult"
              : "Initial Consult",
          creator_id: currentUser?.UserId,
          creator_name: currentUser?.Name
        };

        const sessionId = getSessionId();
        const headers = {
          "Content-Type": "application/json",
          "x-api-key": "76d802e426a2cb28f3760c8c8f669983f67ed775",
        };

        if (sessionId) {
          headers["Cookie"] = `session_id=${sessionId}`;
        }

        const response = await fetch(
          "https://us.uwc.world/new-uwc/uwc_expert/appointment/book-now",
          {
            method: "POST",
            headers,
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        const data = await response.json();
        showSnackbar("Appointment Booked Successfully!", "success");
        handleCloseModal();
      }
    } catch (error) {
      console.error("Error booking appointment:", error);

      // If it's a booking conflict, refresh the available slots
      if (error.message.includes("already booked")) {
        // Clear the selected slot and refresh available slots
        setSelectedSlot("");
        showSnackbar(
          "Time slot no longer available. Please find new slots.",
          "warning"
        );
        // Optionally auto-refresh slots
        setTimeout(() => {
          handleFindSlots();
        }, 1000);
      } else {
        showSnackbar(`Failed to book appointment: ${error.message}`, "error");
      }
    } finally {
      setLoading((prev) => ({ ...prev, booking: false }));
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="add-appointment"
      >
        <Box
          sx={{
            ...style,
            p: 3,
            borderRadius: 4,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            Book Appointment
          </Typography>

          {/* User Search */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Autocomplete
              fullWidth
              options={patientOptions}
              getOptionLabel={(option) => option.name}
              value={selectedPatient}
              onChange={(event, newValue) => {
                setSelectedPatient(newValue);
              }}
              inputValue={patientInputValue}
              onInputChange={(event, newInputValue) => {
                setPatientInputValue(newInputValue);
                debouncedPatientSearch(newInputValue);
              }}
              loading={loading.patients}
              filterOptions={(options, state) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Patient"
                  variant="outlined"
                  sx={{ borderRadius: 3 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box display="flex" alignItems="center" gap={2}>
                    {option.image && (
                      <img
                        src={option.image}
                        alt={option.name}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email} • {option.phone}
                        {option.country && ` • ${option.country}`}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              noOptionsText={
                patientInputValue.length < 2
                  ? "Type at least 2 characters"
                  : "No patients found"
              }
            />
            <IconButton
              onClick={() => setIsUserModalOpen(true)}
              className="PrimaryCTAExp"
              sx={{
                borderRadius: 10,
                color: "white",
              }}
            >
              <AddIcon />
            </IconButton>
          </Box>

          {/* Expert Search */}
          <Box mb={2}>
            <Autocomplete
              fullWidth
              options={expertOptions}
              getOptionLabel={(option) => option.name}
              value={selectedExpert}
              onChange={(event, newValue) => {
                setTimeZone(newValue?.timezone || "");
                handleExpertSelection(newValue);
              }}
              inputValue={expertInputValue}
              onInputChange={(event, newInputValue) => {
                setExpertInputValue(newInputValue);
                debouncedExpertSearch(newInputValue);
              }}
              loading={loading.doctors}
              filterOptions={(options, state) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Expert"
                  variant="outlined"
                  sx={{ borderRadius: 3 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.email} • {option.phone}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText={
                expertInputValue.length < 2
                  ? "Type at least 2 characters"
                  : "No experts found"
              }
            />
            {/* {loadingExpertDetails && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ mt: 1, p: 1 }}
              >
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading expert details...
                </Typography>
              </Box>
            )} */}
          </Box>

          {/* Register As Category - Only show if expert is selected */}
          {availableRegisterAs && availableRegisterAs.length > 0 && (
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Select Category
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 1,
                  width: "100%",
                }}
              >
                {availableRegisterAs.map((registerAs) => (
                  <Button
                    key={registerAs}
                    variant={
                      selectedRegisterAs === registerAs
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => {
                      setSelectedRegisterAs(registerAs);
                      setAppointmentType(""); // Reset appointment type when category changes
                      setDuration(""); // Reset duration
                      setPrice(0); // Reset price when category changes
                    }}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      px: 2,
                      minHeight: 40,
                      fontSize: "0.875rem",
                      textTransform: "none",
                      borderColor:
                        selectedRegisterAs === registerAs
                          ? "primary.main"
                          : "grey.300",
                      backgroundColor:
                        selectedRegisterAs === registerAs
                          ? "primary.main"
                          : "transparent",
                      color:
                        selectedRegisterAs === registerAs
                          ? "white"
                          : "text.primary",
                      "&:hover": {
                        backgroundColor:
                          selectedRegisterAs === registerAs
                            ? "primary.dark"
                            : "grey.50",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    {registerAs}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* Appointment Type - Only show if register_as is selected */}
          {availableRegisterAs && availableRegisterAs.length > 0 && (
            <Box mb={2}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Appointment Type
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Button
                  variant={
                    appointmentType === "initial" ? "contained" : "outlined"
                  }
                  onClick={() => {
                    handleAppointmentTypeSelection("initial");
                  }}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 2,
                    minHeight: 40,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderColor:
                      appointmentType === "initial"
                        ? "primary.main"
                        : "grey.300",
                    backgroundColor:
                      appointmentType === "initial"
                        ? "primary.main"
                        : "transparent",
                    color:
                      appointmentType === "initial" ? "white" : "text.primary",
                    "&:hover": {
                      backgroundColor:
                        appointmentType === "initial"
                          ? "primary.dark"
                          : "grey.50",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  Initial
                </Button>
                <Button
                  variant={
                    appointmentType === "follow-up" ? "contained" : "outlined"
                  }
                  onClick={() => {
                    handleAppointmentTypeSelection("follow-up");
                  }}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 2,
                    minHeight: 40,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    borderColor:
                      appointmentType === "follow-up"
                        ? "primary.main"
                        : "grey.300",
                    backgroundColor:
                      appointmentType === "follow-up"
                        ? "primary.main"
                        : "transparent",
                    color:
                      appointmentType === "follow-up"
                        ? "white"
                        : "text.primary",
                    "&:hover": {
                      backgroundColor:
                        appointmentType === "follow-up"
                          ? "primary.dark"
                          : "grey.50",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  Follow-up
                </Button>
              </Box>
            </Box>
          )}

          {/* Expert Details Display - Show after expert selection */}
          {expertDetails &&
            nextAvailableSlot &&
            formatNextAvailableSlot(nextAvailableSlot) && (
              <Box
                mb={2}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 1, fontWeight: "medium" }}
                >
                  Next Available Slot
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography
                    variant="body2"
                    sx={{ color: "success.main", fontWeight: "medium" }}
                  >
                    {formatNextAvailableSlot(nextAvailableSlot)}
                  </Typography>
                </Box>
              </Box>
            )}

          {/* Slot Type Selection */}
          <Box mb={2}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary" }}>
              Slot Selection Type
            </Typography>
            <ToggleButtonGroup
              value={isCustomSlot ? "custom" : "regular"}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setIsCustomSlot(newValue === "custom");
                  setSelectedSlot("");
                  setCustomTime("");
                }
              }}
              sx={{ width: "100%" }}
            >
              <ToggleButton 
                value="regular" 
                sx={{ 
                  flex: 1,
                  textTransform: "none",
                  borderRadius: "8px",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    }
                  }
                }}
              >
                Regular Slots
              </ToggleButton>
              <ToggleButton 
                value="custom" 
                sx={{ 
                  flex: 1,
                  textTransform: "none",
                  borderRadius: "8px",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    }
                  }
                }}
              >
                Custom Slot
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Date and Time Selection */}
          {isCustomSlot ? (
            <Box display="flex" gap={2} mb={2}>
              <TextField
                type="date"
                label="Appointment Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                type="time"
                label="Appointment Time"
                value={customTime}
                onChange={(e) => {
                  setCustomTime(e.target.value);
                  setSelectedSlot(e.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          ) : (
            <>
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  type="date"
                  label="Appointment Date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleFindSlots}
                  className="PrimaryCTAExp"
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    minWidth: 120,
                    height: 56,
                  }}
                  disabled={appointmentType === "" || loading?.slots}
                >
                  {loading.slots ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Find Slots"
                  )}
                </Button>
              </Box>

              {/* Time Slots */}
              <Box mb={2} sx={{ width: "100%" }}>
                {loading.slots ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{ width: "100%", justifyContent: "center", py: 2 }}
                  >
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary">
                      Fetching available slots...
                    </Typography>
                  </Box>
                ) : availableSlots.length > 0 ? (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "text.secondary" }}
                      >
                        Available Time Slots ({availableSlots.length})
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={selectedSlot === slot ? "contained" : "outlined"}
                          onClick={() => setSelectedSlot(slot)}
                          sx={{
                            borderRadius: 2,
                            py: 1,
                            px: 2,
                            minHeight: 40,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            borderColor:
                              selectedSlot === slot ? "primary.main" : "grey.300",
                            backgroundColor:
                              selectedSlot === slot
                                ? "primary.main"
                                : "transparent",
                            color: selectedSlot === slot ? "white" : "text.primary",
                            "&:hover": {
                              backgroundColor:
                                selectedSlot === slot ? "primary.dark" : "grey.50",
                              borderColor: "primary.main",
                            },
                          }}
                        >
                          {slot}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 3,
                      textAlign: "center",
                      border: "1px dashed",
                      borderColor: "grey.300",
                      borderRadius: 2,
                      backgroundColor: "grey.50",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Click "Find Slots" to see available time slots
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Duration and Price Display - Only show if appointment type is selected */}
          {duration && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Duration: {duration} minutes &nbsp;
                {getPriceForSelection() && (
                  <span> • Price: ${getPriceForSelection()}</span>
                )}
              </Typography>
            </Box>
          )}

          {/* Pricing - Only show if payment method is cash */}
          {paymentMethod === "cash" && (
            <>
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={price === 0 ? '' : price}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setPrice(0);
                    } else {
                      setPrice(Number(value));
                    }
                  }}
                  sx={{
                    "& input[type=number]": {
                      "-moz-appearance": "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                  }}
                />
                <TextField
                  fullWidth
                  label="Discount %"
                  type="number"
                  value={discount === 0 ? '' : discount}
                  inputProps={{
                    min: 0,
                    max: 100
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setDiscount(0);
                    } else {
                      const numValue = Number(value);
                      if (numValue <= 100) {
                        setDiscount(numValue);
                      }
                    }
                  }}
                  sx={{
                    "& input[type=number]": {
                      "-moz-appearance": "textfield",
                    },
                    "& input[type=number]::-webkit-outer-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                    "& input[type=number]::-webkit-inner-spin-button": {
                      "-webkit-appearance": "none",
                      margin: 0,
                    },
                  }}
                />
              </Box>
              <Typography mt={1}>Final Price: ${finalPrice || 0}</Typography>
            </>
          )}

          {/* Payment Method */}
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              name="payment-method"
            >
              <FormControlLabel
                value="Complimentary"
                control={<Radio />}
                label="Complimentary"
              />
              <FormControlLabel
                value="stripe"
                control={<Radio />}
                label="Online Payment"
              />
              <FormControlLabel
                value="cash"
                control={<Radio />}
                label="Cash/Card"
              />
            </RadioGroup>
          </Box>

          {/* Book Button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="text"
              onClick={handleBook}
              className=" PrimaryCTAExp"
              disabled={
                loading.booking ||
                !selectedPatient ||
                !selectedExpert ||
                // (!isCustomSlot || (!selectedRegisterAs || !appointmentType))||
                !paymentMethod ||
                (isCustomSlot ? !customTime : !selectedSlot) ||
                (paymentMethod === "cash" && !price)
              }
            >
              {loading.booking ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "BOOK"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      <UserCreationModal
        open={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
